import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import LoopBuilder from "@/components/LoopBuilder";
import {
  Waypoints, Plus, MapPin, Briefcase, GraduationCap, Target, Radar,
  RefreshCw, Pencil, Trash2, ExternalLink, KeyRound, Loader2, Layers, Inbox,
} from "lucide-react";

const Stat = ({ icon: Icon, label, value, accent = "text-slate-700" }) => (
  <div className="flex items-center gap-1.5 text-xs">
    <Icon className="w-3.5 h-3.5 text-slate-500" />
    <span className="text-slate-500">{label}:</span>
    <span className={`font-medium ${accent}`}>{value}</span>
  </div>
);

export default function LoopSeries() {
  const navigate = useNavigate();
  const [loops, setLoops] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconcileError, setReconcileError] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cfg, list] = await Promise.all([api.get("/loops/config"), api.get("/loops")]);
      setConfig(cfg.data);
      setLoops(list.data.loops);
      setReconcileError(list.data.reconcile_error);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async (loop) => {
    setRefreshingId(loop.id);
    try {
      const { data } = await api.post(`/loops/${loop.id}/refresh`);
      setLoops((prev) => prev.map((l) => (l.id === loop.id ? data.loop : l)));
      toast.success(`Synced: ${data.loop.collected_count} jobs, ${data.loop.matched_count} matches.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRefreshingId(null);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/loops/${deleteTarget.id}`);
      setLoops((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      toast.success("Apply Series search deleted.");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const onSaved = (loop) => {
    setLoops((prev) => {
      const exists = prev.find((l) => l.id === loop.id);
      return exists ? prev.map((l) => (l.id === loop.id ? loop : l)) : [loop, ...prev];
    });
  };

  const count = (v) => (v === null || v === undefined ? "—" : v);

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="loop-series-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-amber-500 text-xs font-mono uppercase tracking-widest">
            <Waypoints className="w-4 h-4" /> Automated Job Discovery
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display mt-2">Apply Series</h1>
          <p className="text-slate-500 mt-1">Create Apply Series searches, then review the jobs and matches they generate.</p>
        </div>
        <Button
          data-testid="build-loop-button"
          onClick={() => { setEditing(null); setBuilderOpen(true); }}
          disabled={config && !config.api_configured}
          className="bg-amber-500 text-black hover:bg-amber-400 font-semibold transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" /> Build Loop
        </Button>
      </div>

      {config && !config.api_configured && (
        <div data-testid="loopcv-not-configured" className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
          <KeyRound className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-800">
            <b>Discovery engine not connected.</b> Apply Series creation and job/match fetching are temporarily
            unavailable. No demo data is shown — this reflects the real connection state.
          </div>
        </div>
      )}

      {reconcileError && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          Could not sync with the discovery engine: {reconcileError}
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-slate-500"><Loader2 className="w-8 h-8 mx-auto animate-spin text-amber-500" /></div>
      ) : loops.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center">
          <Radar className="w-12 h-12 mx-auto text-slate-400" />
          <h3 className="mt-4 font-semibold text-lg">No loops yet</h3>
          <p className="text-slate-500 text-sm mt-1 max-w-sm mx-auto">
            {config?.api_configured
              ? "Build your first Apply Series search to start collecting jobs and matches."
              : "Discovery engine offline — contact your administrator."}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {loops.map((loop, i) => (
            <motion.div
              key={loop.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              data-testid={`loop-card-${loop.id}`}
              className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 hover:border-slate-300 transition-colors"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <button
                    onClick={() => navigate(`/app/loops/${loop.id}`)}
                    data-testid={`open-loop-${loop.id}`}
                    className="text-lg font-semibold hover:text-amber-400 transition-colors text-left flex items-center gap-2"
                  >
                    {loop.title}
                    <ExternalLink className="w-4 h-4 opacity-50" />
                  </button>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2">
                    <Stat icon={MapPin} label="Location" value={loop.location} />
                    <Stat icon={Briefcase} label="Type" value={loop.type} />
                    <Stat icon={GraduationCap} label="Level" value={loop.level} />
                    <Stat icon={Target} label="Match" value={loop.matchlevel ?? "—"} accent="text-amber-400" />
                    {loop.auto_sync && (
                      <span className="flex items-center gap-1 text-xs text-emerald-300"><RefreshCw className="w-3 h-3" /> Auto-sync on</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" data-testid={`refresh-loop-${loop.id}`} onClick={() => refresh(loop)}
                    disabled={refreshingId === loop.id} className="border-slate-300 text-slate-700 hover:text-amber-400">
                    <RefreshCw className={`w-4 h-4 ${refreshingId === loop.id ? "animate-spin" : ""}`} />
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`edit-loop-${loop.id}`}
                    onClick={() => { setEditing(loop); setBuilderOpen(true); }}
                    className="border-slate-300 text-slate-700 hover:text-orange-300"><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" data-testid={`delete-loop-${loop.id}`}
                    onClick={() => setDeleteTarget(loop)}
                    className="border-slate-300 text-slate-700 hover:text-rose-300"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>

              {(loop.keywords?.length > 0 || loop.scrapers?.length > 0) && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {loop.keywords?.map((k) => (
                    <Badge key={k} className="bg-orange-500/10 text-amber-400 border border-orange-500/30">{k}</Badge>
                  ))}
                  {loop.scrapers?.map((s) => (
                    <Badge key={s} className="bg-orange-500/10 text-orange-300 border border-orange-500/30">{s}</Badge>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-4 border-t border-slate-200">
                <div>
                  <div className="flex items-center gap-1.5 text-emerald-300"><Layers className="w-4 h-4" /><span className="text-xl font-bold font-display">{count(loop.collected_count)}</span></div>
                  <p className="text-xs text-slate-500 mt-0.5">Collected jobs</p>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-amber-300"><Inbox className="w-4 h-4" /><span className="text-xl font-bold font-display">{count(loop.matched_count)}</span></div>
                  <p className="text-xs text-slate-500 mt-0.5">Matched jobs</p>
                </div>
                <div className="col-span-2 text-xs space-y-1 font-mono">
                  <div className="text-slate-500">Engine ID: <span className="text-slate-700">{loop.loop_id || "—"}</span></div>
                  <div className="text-slate-500">Apply Series ID: <span className="text-slate-700">{loop.external_id}</span></div>
                  <div className="text-slate-500">Last sync: <span className="text-slate-700">{loop.last_synced_at ? new Date(loop.last_synced_at).toLocaleString() : "Never"}</span></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <LoopBuilder
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        onSaved={onSaved}
        mode={editing ? "edit" : "create"}
        initial={editing}
        loopRef={editing?.id}
        options={config}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white border-slate-200 text-slate-900" data-testid="delete-loop-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this loop?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              This permanently deletes "{deleteTarget?.title}" and stops all job collection for it. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-300 text-slate-700 hover:bg-slate-100">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-loop" onClick={confirmDelete} disabled={deleting}
              className="bg-rose-500 text-white hover:bg-rose-400">
              {deleting ? "Deleting…" : "Delete loop"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
